import { Component, OnInit, OnDestroy, ElementRef, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';

import { CdkDrag, CdkDragEnd } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FullCalendarModule, FullCalendarComponent } from '@fullcalendar/angular';
import { CalendarOptions, EventInput, DateSelectArg, EventClickArg } from '@fullcalendar/core';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import huLocale from '@fullcalendar/core/locales/hu';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';

import { CalendarService } from '../../../services/calendar.service';
import { AuthService } from '../../../services/auth.service';
import { UserPreferencesService } from '../../../services/user-preferences.service';
import { CalendarEvent, CalendarEventCreate, CalendarEventUpdate, CalendarVisibleUser } from '../../../interfaces/calendar-event.interface';
import { CalendarEventDialogComponent } from '../../../components/calendar/calendar-event-dialog/calendar-event-dialog.component';
import { CalendarEventDetailDialogComponent } from '../../../components/calendar/calendar-event-detail-dialog/calendar-event-detail-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../components/confirm-dialog/confirm-dialog';

const STORAGE_KEY = 'floating-calendar-btn-y';
const USER_COLORS = [
  '#7B1FA2', '#00796B', '#E64A19', '#5D4037',
  '#455A64', '#C2185B', '#1976D2', '#388E3C',
];

@Component({
  selector: 'app-floating-calendar-button',
  imports: [
    CdkDrag,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    FullCalendarModule
],
  templateUrl: './floating-calendar-button.component.html',
  styleUrl: './floating-calendar-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FloatingCalendarButtonComponent implements OnInit, OnDestroy {
  @ViewChild('dragElement') dragElement!: ElementRef<HTMLElement>;
  @ViewChild('panelCalendar') panelCalendar!: FullCalendarComponent;
  @ViewChild('panelBody') panelBody!: ElementRef<HTMLElement>;

  dragPosition = { x: 0, y: 0 };
  isPanelOpen = false;
  isCalendarReady = false;
  isLoading = false;
  todayEvents: EventInput[] = [];
  currentUserId = 0;
  visibleUsers: CalendarVisibleUser[] = [];
  selectedUserIds: Set<number> = new Set();
  slotDuration = '00:15:00';

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
    locale: huLocale,
    initialView: 'timeGridDay',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'timeGridDay,listDay',
    },
    buttonText: {
      today: 'Ma',
      day: 'Nap',
      list: 'Lista',
    },
    height: '100%',
    selectable: true,
    editable: false,
    selectMirror: true,
    dayMaxEvents: true,
    weekNumbers: true,
    weekNumberFormat: { week: 'short' },
    nowIndicator: true,
    scrollTime: this.calculateScrollTime(),
    scrollTimeReset: false,
    slotMinTime: '06:00:00',
    slotMaxTime: '20:00:00',
    slotDuration: '00:15:00',
    allDayText: 'Egész nap',
    noEventsText: 'Nincs esemény ma',
    moreLinkText: 'további',
    events: [],
    select: (info: DateSelectArg) => this.onDateSelect(info),
    eventClick: (info: EventClickArg) => this.onEventClick(info),
    datesSet: (info) => {
      setTimeout(() => this.loadEventsForRange(info.start, info.end));
    },
    eventContent: (arg) => {
      const calEvent = arg.event.extendedProps?.['calendarEvent'];
      if (calEvent?.source === 'reminder') {
        const start = arg.event.start;
        const timeStr = start
          ? `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')}`
          : '';

        const frame = document.createElement('div');
        frame.className = 'fc-event-main-frame';

        const timeEl = document.createElement('div');
        timeEl.className = 'fc-event-time';
        timeEl.textContent = timeStr;
        frame.appendChild(timeEl);

        const titleContainer = document.createElement('div');
        titleContainer.className = 'fc-event-title-container';
        const titleEl = document.createElement('div');
        titleEl.className = 'fc-event-title fc-sticky';
        titleEl.textContent = arg.event.title;
        titleContainer.appendChild(titleEl);
        frame.appendChild(titleContainer);

        return { domNodes: [frame] };
      }
      return true;
    },
  };

  private isDragging = false;
  private eventsSubscription?: Subscription;
  private slotDurationSubscription?: Subscription;
  private calendarSelectionSubscription?: Subscription;

  constructor(
    private router: Router,
    private calendarService: CalendarService,
    private authService: AuthService,
    private userPreferencesService: UserPreferencesService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    if (user) {
      this.currentUserId = user.id;
    }
    this.loadVisibleUsers();
    this.restoreSlotDuration();
    this.subscribeToCalendarSelectionChanges();

    const savedY = localStorage.getItem(STORAGE_KEY);
    if (savedY) {
      this.dragPosition = { x: 0, y: parseInt(savedY, 10) };
    }
    this.clampPosition();
  }

  ngOnDestroy(): void {
    this.eventsSubscription?.unsubscribe();
    this.slotDurationSubscription?.unsubscribe();
    this.calendarSelectionSubscription?.unsubscribe();
  }

  onDragStarted(): void {
    this.isDragging = true;
  }

  onDragEnded(event: CdkDragEnd): void {
    const currentY = this.dragPosition.y + event.distance.y;
    this.dragPosition = { x: 0, y: currentY };
    this.clampPosition();
    localStorage.setItem(STORAGE_KEY, this.dragPosition.y.toString());

    setTimeout(() => {
      this.isDragging = false;
    }, 50);
  }

  onClick(): void {
    if (!this.isDragging) {
      this.togglePanel();
    }
  }

  togglePanel(): void {
    this.isPanelOpen = !this.isPanelOpen;
    if (this.isPanelOpen) {
      setTimeout(() => {
        this.isCalendarReady = true;
        this.cdr.markForCheck();
        // Timegrid görgetése az aktuális időponthoz a renderelés után
        setTimeout(() => this.scrollToCurrentTime(), 100);
      }, 400);
    } else {
      this.isCalendarReady = false;
    }
  }

  closePanel(): void {
    this.isPanelOpen = false;
    this.isCalendarReady = false;
    this.cdr.markForCheck();
  }

  navigateToCalendar(): void {
    this.router.navigate(['/calendar']);
    this.closePanel();
  }

  private onEventClick(info: EventClickArg): void {
    const calEvent: CalendarEvent = info.event.extendedProps['calendarEvent'];
    if (!calEvent) return;

    const dialogRef = this.dialog.open(CalendarEventDetailDialogComponent, {
      width: '450px',
      panelClass: 'crm-dialog',
      data: { event: calEvent, currentUserId: this.currentUserId },
    });

    dialogRef.afterClosed().subscribe((result: { action: string; data?: any } | null) => {
      if (!result) return;

      if (result.action === 'edit') {
        this.openEditDialog(calEvent);
      } else if (result.action === 'delete') {
        this.deleteEvent(calEvent);
      } else if (result.action === 'statusChange') {
        this.updateEventStatus(calEvent, result.data);
      }
    });
  }

  private openEditDialog(calEvent: CalendarEvent): void {
    const dialogRef = this.dialog.open(CalendarEventDialogComponent, {
      width: '500px',
      panelClass: 'crm-dialog',
      data: {
        mode: 'edit' as const,
        event: calEvent,
        visibleUsers: this.visibleUsers,
        currentUserId: this.currentUserId,
        slotDuration: this.slotDuration,
      },
    });

    dialogRef.afterClosed().subscribe((result: CalendarEventUpdate | null) => {
      if (!result) return;
      this.calendarService.updateEvent(calEvent.source_id, result).subscribe({
        next: () => {
          this.snackBar.open('Esemény módosítva', 'OK', { duration: 3000 });
          this.reloadCurrentEvents();
        },
        error: () => this.snackBar.open('Hiba a módosításkor', 'OK', { duration: 3000 }),
      });
    });
  }

  private deleteEvent(calEvent: CalendarEvent): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: calEvent.source === 'reminder' ? 'Emlékeztető törlése' : 'Esemény törlése',
        message: `Biztosan törölni szeretnéd a(z) "${calEvent.title}" ${calEvent.source === 'reminder' ? 'emlékeztetőt' : 'eseményt'}?`,
        type: 'delete',
      } as ConfirmDialogData,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.calendarService.deleteEvent(calEvent.source_id).subscribe({
        next: () => {
          this.snackBar.open('Esemény törölve', 'OK', { duration: 3000 });
          this.reloadCurrentEvents();
        },
        error: () => this.snackBar.open('Hiba a törléskor', 'OK', { duration: 3000 }),
      });
    });
  }

  private updateEventStatus(calEvent: CalendarEvent, status: string): void {
    this.calendarService.updateEvent(calEvent.source_id, { status: status as any }).subscribe({
      next: () => {
        this.snackBar.open('Státusz módosítva', 'OK', { duration: 3000 });
        this.reloadCurrentEvents();
      },
      error: () => this.snackBar.open('Hiba a státusz módosításkor', 'OK', { duration: 3000 }),
    });
  }

  private onDateSelect(info: DateSelectArg): void {
    const dialogRef = this.dialog.open(CalendarEventDialogComponent, {
      width: '500px',
      panelClass: 'crm-dialog',
      data: {
        mode: 'create' as const,
        startDate: info.start,
        endDate: info.end,
        allDay: info.allDay,
        visibleUsers: this.visibleUsers,
        currentUserId: this.currentUserId,
        slotDuration: this.slotDuration,
      },
    });

    dialogRef.afterClosed().subscribe((result: CalendarEventCreate | null) => {
      if (!result) return;
      this.calendarService.createEvent(result).subscribe({
        next: () => {
          this.snackBar.open('Esemény létrehozva', 'OK', { duration: 3000 });
          this.reloadCurrentEvents();
        },
        error: () => this.snackBar.open('Hiba az esemény létrehozásakor', 'OK', { duration: 3000 }),
      });
    });
  }

  private reloadCurrentEvents(): void {
    const calendarApi = this.panelCalendar?.getApi();
    if (calendarApi) {
      const view = calendarApi.view;
      this.loadEventsForRange(view.activeStart, view.activeEnd);
    }
  }

  private loadVisibleUsers(): void {
    this.calendarService.getVisibleUsers().subscribe({
      next: (res) => {
        this.visibleUsers = res.users || [];
        this.restoreCalendarSelection();
      },
    });
  }

  private restoreCalendarSelection(): void {
    this.userPreferencesService.getCalendarSelection().subscribe({
      next: (res) => {
        const savedIds = res.calendars || [];
        const validUserIds = new Set(this.visibleUsers.map(u => u.user_id));
        this.selectedUserIds = new Set(savedIds.filter(id => validUserIds.has(id)));
      },
    });
  }

  /** Feliratkozás a naptár modul kiválasztás-változásaira (valós idejű szinkron) */
  private subscribeToCalendarSelectionChanges(): void {
    this.calendarSelectionSubscription = this.userPreferencesService.calendarSelection$.subscribe((savedIds) => {
      const validUserIds = new Set(this.visibleUsers.map(u => u.user_id));
      this.selectedUserIds = new Set(savedIds.filter(id => validUserIds.has(id)));
      this.reloadCurrentEvents();
      this.cdr.markForCheck();
    });
  }

  private restoreSlotDuration(): void {
    // Először API-ból tölti be a mentett értéket (ha a calendar-wrapper még nem tette meg)
    this.userPreferencesService.getCalendarSlotDuration().subscribe({
      next: (res) => {
        const loaded = res.slot_duration || '00:15:00';
        this.userPreferencesService.notifySlotDurationLoaded(loaded);
      },
      error: () => { /* hiba esetén a BehaviorSubject aktuális értéke marad */ }
    });

    // Valós idejű stream: betöltéskor és minden változáskor frissít
    this.slotDurationSubscription = this.userPreferencesService.slotDuration$.subscribe((slotDuration) => {
      this.slotDuration = slotDuration;
      this.calendarOptions = { ...this.calendarOptions, slotDuration: this.slotDuration };
      this.cdr.markForCheck();
    });
  }

  private loadEventsForRange(start: Date, end: Date): void {
    this.isLoading = true;
    this.cdr.markForCheck();
    this.eventsSubscription?.unsubscribe();
    this.eventsSubscription = this.calendarService.getEvents(start.toISOString(), end.toISOString()).subscribe({
      next: (response) => {
        const myEvents = (response?.events || []).map((ev: CalendarEvent) => this.mapEvent(ev));

        // Megosztott események betöltése
        const userIds = Array.from(this.selectedUserIds);
        if (userIds.length > 0) {
          this.calendarService.getSharedEvents(start.toISOString(), end.toISOString(), userIds).subscribe({
            next: (sharedRes) => {
              const sharedEvents = (sharedRes.events || []).map((ev: CalendarEvent) => {
                const fc = this.mapEvent(ev);
                const userIndex = this.visibleUsers.findIndex(u => u.user_id === ev.user_id);
                if (userIndex >= 0) {
                  fc.color = USER_COLORS[userIndex % USER_COLORS.length];
                  fc.textColor = '#fff';
                }
                return fc;
              });
              this.todayEvents = [...myEvents, ...sharedEvents];
              this.calendarOptions = { ...this.calendarOptions, events: this.todayEvents };
              this.isLoading = false;
              this.cdr.markForCheck();
            },
            error: () => {
              this.todayEvents = myEvents;
              this.calendarOptions = { ...this.calendarOptions, events: this.todayEvents };
              this.isLoading = false;
              this.cdr.markForCheck();
            },
          });
        } else {
          this.todayEvents = myEvents;
          this.calendarOptions = { ...this.calendarOptions, events: this.todayEvents };
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      },
      error: () => {
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private mapEvent(ev: CalendarEvent): EventInput {
    let end = ev.end || undefined;
    if (ev.source === 'reminder' && ev.start && !ev.allDay) {
      const startDate = new Date(ev.start);
      end = new Date(startDate.getTime() + 15 * 60 * 1000).toISOString();
    }
    return {
      id: ev.id,
      title: ev.title,
      start: ev.start,
      end,
      allDay: ev.allDay,
      color: ev.color || (ev.source === 'reminder' ? '#FFC107' : '#1976D2'),
      textColor: ev.source === 'reminder' ? '#333' : '#fff',
      extendedProps: { calendarEvent: ev },
    };
  }

  private scrollToCurrentTime(): void {
    const container = this.panelBody?.nativeElement;
    if (!container) return;

    // Keresés: now-indicator vonal vagy időpont alapú számítás
    const nowIndicator = container.querySelector('.fc-timegrid-now-indicator-line') as HTMLElement;
    if (nowIndicator) {
      const containerRect = container.getBoundingClientRect();
      const indicatorRect = nowIndicator.getBoundingClientRect();
      const offset = indicatorRect.top - containerRect.top + container.scrollTop;
      // Középre igazítás: container magasságának felét levonjuk
      const scrollTarget = offset - container.clientHeight / 3;
      container.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' });
    } else {
      // Fallback: idő alapú pozíció számítás
      const now = new Date();
      const slotMinHour = 6;
      const slotMaxHour = 20;
      const currentHour = Math.max(slotMinHour, Math.min(now.getHours() + now.getMinutes() / 60, slotMaxHour));
      const totalHours = slotMaxHour - slotMinHour;
      const ratio = (currentHour - slotMinHour) / totalHours;
      const scrollTarget = container.scrollHeight * ratio - container.clientHeight / 3;
      container.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' });
    }
  }

  private calculateScrollTime(): string {
    const now = new Date();
    let h = now.getHours() - 1;
    if (h < 6) h = 6;
    if (h > 18) h = 18;
    return `${h.toString().padStart(2, '0')}:00:00`;
  }

  private clampPosition(): void {
    const headerHeight = 64;
    const buttonHeight = 56;
    const minY = -(window.innerHeight / 2 - headerHeight - buttonHeight / 2);
    const maxY = window.innerHeight / 2 - buttonHeight;

    if (this.dragPosition.y < minY) {
      this.dragPosition = { x: 0, y: minY };
    } else if (this.dragPosition.y > maxY) {
      this.dragPosition = { x: 0, y: maxY };
    }
  }
}
